import {
  from,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider as Provider,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getCookie, deleteCookie, setCookie } from "cookies-next";
import packageData from "package.json";
import { useMemo } from "react";
import { toast } from "react-toastify";

import { isProd } from "@config/config";

const isServer = typeof window === "undefined";
// const windowApolloState = !isServer && window.__NEXT_DATA__.apolloState;

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";
let apolloClient;
// const logLink = new ApolloLink((operation, forward) => {
//   return forward(operation);
// });

const httpLink = createUploadLink({
  uri: process.env.NEXT_PUBLIC_LUXEXPRESS_API_ENDPOINT,
  credentials: "include",
});

const httpLinkV2 = createUploadLink({
  uri: process.env.NEXT_PUBLIC_LUXEXPRESS_API_ENDPOINT_V2,
  credentials: "include",
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = getCookie("token");
  const shoppingBasketId = getCookie("shopping-basket-id");
  const modificationSessionId = getCookie("msi");
  const platform = "web-next";

  const corporateToken = getCookie("corporate-token");

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      platform,
      version: packageData.version,
      ...(token
        ? {
            authorization: `JWT ${token}`,
          }
        : {}),
      ...(modificationSessionId
        ? {
            "modification-session-id": modificationSessionId,
          }
        : {}),
      ...(shoppingBasketId
        ? {
            "shopping-basket-id": shoppingBasketId,
          }
        : {}),
      ...(corporateToken
        ? {
            "Authorization-Corporate": corporateToken,
          }
        : {}),
    },
  };
});

const corporateTokenExpiredLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (operation.getContext().preferredLanguageOperation) return response;
    if (response && response.data) {
      const checkTokenExpired = (obj) => {
        if (!obj) return false;

        for (const key in obj) {
          const value = obj[key];

          if (value && typeof value === "object") {
            if (
              value.ErrorCode === "TOKEN_EXPIRED" ||
              value.ErrorCode === "INVALID_TOKEN"
            ) {
              return true;
            }
            if (checkTokenExpired(value)) {
              return true;
            }
          }
        }
        return false;
      };

      if (checkTokenExpired(response.data)) {
        setCookie("extraLogout", "true");
      }
    }

    return response;
  });
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (operation.getContext().preferredLanguageOperation) return;
    try {
      if (networkError) {
        const { statusCode = "" } = networkError;
        console.error("Network error:", networkError);
      }

      if (graphQLErrors) {
        const isUnauthorized = graphQLErrors.some(
          ({ message }) => message === "Please sign in",
        );

        if (isUnauthorized && !isServer) {
          const isProfilePage = /\/profile(\/|$)/.test(
            window.location.pathname,
          );

          if (isProfilePage) {
            setCookie("extraLogout", "true");
            return;
          }
        }

        if (!isProd) {
          graphQLErrors.forEach(({ message, locations, path }) => {
            toast.error(
              `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
                locations,
              )}, Path: ${path}`,
            );
          });
        }
      }

      return forward(operation);
    } catch (e) {
      console.error("ErrorLink, error: ", e);
    }
  },
);

function createApolloClient() {
  return new ApolloClient({
    // link: from([logLink, authLink, errorLink, httpLink]), // with log
    link: from([
      authLink,
      corporateTokenExpiredLink,
      errorLink,
      ApolloLink.split(
        (operation) => {
          return operation.getContext().clientName === "v2";
        },
        httpLinkV2,
        httpLink,
      ),
    ]),
    cache: new InMemoryCache(),
    ssrMode: isServer,
    defaultOptions: {
      mutate: {
        context: {
          hasUpload: true,
        },
      },
    },
    // queryDeduplication: false,
    // defaultOptions: {
    //   watchQuery: {
    //     fetchPolicy: "cache-and-network",
    //   },
    // },
    // watchQuery: {
    //   ssr: false,
    //   errorPolicy: "all",
    // },
    // query: {
    //   ssr: false,
    //   errorPolicy: "all",
    // },
    // mutate: {
    //   errorPolicy: "all",
    // },

    // name: "luxexpress-apollo-client", // this line breaks CORS
    // version: "1.0", // this line breaks CORS
  });
}

export function ApolloProvider({ showError, children, client }) {
  const clientA = useMemo(() => client, [showError]);

  // const apolloClient = useMemo(
  //   () => new ApolloClient({
  //     ... // Same configuration as before
  //   }),
  //   [showError]
  // )

  return <Provider client={clientA}>{children}</Provider>;
}

// export function initializeApollo(initialState) {
//   const _apolloClient = apolloClient ?? createApolloClient();
//
//   // If your page has Next.js data fetching methods that use Apollo Client, the initial state
//   // gets hydrated here
//   if (initialState) {
//     // Get existing cache, loaded during client side data fetching
//     const existingCache = _apolloClient.extract();
//
//     // Merge the existing cache into data passed from getStaticProps/getServerSideProps
//     const data = merge(initialState, existingCache, {
//       // combine arrays using object equality (like in sets)
//       arrayMerge: (destinationArray, sourceArray) => [
//         ...sourceArray,
//         ...destinationArray.filter((d) =>
//           sourceArray.every((s) => !isEqual(d, s))
//         ),
//       ],
//     });
//
//     // Restore the cache with the merged data
//     _apolloClient.cache.restore(data);
//   }
//
//   // For SSG and SSR always create a new Apollo Client
//   if (isServer) return _apolloClient;
//
//   // Create the Apollo Client once in the client
//   if (!apolloClient) apolloClient = _apolloClient;
//
//   return _apolloClient;
// }

// export function initializeApollo(initialState) {
//   const client = apolloClient ?? createApolloClient();
//
//   if (initialState) {
//     // Merge the initial state with the existing cache
//     client.cache.restore(initialState);
//   }
//
//   // For SSG and SSR, always create a new Apollo Client
//   if (isServer) return client;
//
//   // Create the Apollo Client once in the client
//   if (!apolloClient) apolloClient = client;
//
//   return client;
// }

export function initializeApollo(initialState = null) {
  const _apolloClient = apolloClient ?? createApolloClient();

  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  if (isServer) return _apolloClient;
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client, pageProps) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state), [state]);
  return store;
}
